import {AfterViewInit, Component, ElementRef, EventEmitter, Inject, Input, isDevMode, OnChanges, OnDestroy, OnInit, Output, Renderer2, ViewEncapsulation, DOCUMENT, ChangeDetectionStrategy} from '@angular/core';
import { HttpClient } from '@angular/common/http';
import {AsyncTranslationLoader} from '../../app/async-translation-loader';
import {StateService} from '../../app/services/state.service';
import {ApiService} from '../../app/services/api.service';
import {UntypedFormControl, UntypedFormGroup} from '@angular/forms';
import {EMPTY, Subscription} from 'rxjs';
import {environment} from '../../environments/environment';
import {OrderComponent} from '../../app/form/order/order.component';

import {ActivatedRoute, Router} from '@angular/router';
import {NgxSmartModalService} from 'ngx-smart-modal';
import {TranslateService} from '@ngx-translate/core';
import {CoolLocalStorage} from '@angular-cool/storage';
import {FieldErrorService} from '../../app/services/field-error.service';
import {AnalyticsService} from '../../app/services/analytics.service';
import {DomSanitizer, Title} from '@angular/platform-browser';
import {VisitorService} from '../../app/services/visitor.service';
import {LANGUAGES} from '../../app/data/languages';
import {CustomerService} from "../../app/services/customer.service";
import * as ColorContrastChecker from 'color-contrast-checker';
import {MatIconRegistry} from "@angular/material/icon";
import {AppComponent} from '../../app/app.component';
import {applyMobileTheme} from '../../app/data/mobile-themes';

const apiFormIdProvider = (state: StateService, route: ActivatedRoute) => {
  if (route.parent && route.parent.snapshot) {
    return route.parent.snapshot.params.formId;
  } else {
    return state.formId;
  }
};

interface Styles {
  colors: any;
  // Selects a mobile product-card preset; see app/data/mobile-themes.ts.
  mobileTheme?: string;
}

@Component({
    selector: 'app-widget',
    templateUrl: '../../app/form/order/order.component.html',
    encapsulation: ViewEncapsulation.Emulated,
    styleUrls: ['./inline-webbooker.component.scss',
        '../../app/form/order/order.component.scss'],
    providers: [
        {
            provide: 'apiModel',
            useValue: 'orders'
        },
        {
            provide: 'apiFormId',
            useFactory: apiFormIdProvider,
            deps: [StateService, ActivatedRoute]
        },
        ApiService, CustomerService
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class InlineWebbookerComponent extends OrderComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {
  formConfig = 'webbooker';
  formStyle = 'inline';
  @Input() formid: string;
  @Input() language: string;
  @Input() navigation: string = 'inline';
  @Input() showtopbar: any = false;
  @Input() showbreadcrumbs: any = true;
  @Input() showlogo: any = true;
  @Input() buttonstyle: string = 'rounded';
  @Input() headercolor: string = '#597db0';
  @Input() textcolor: string = '#000000';
  @Input() backgroundcolor: string = 'transparent';
  @Input() origin: string = 'inline-widget';
  @Input() widgettype: string = 'inline-widget';
  @Input() getSettings: any;
  @Output() getSettingsChange = new EventEmitter<any>();

  nActivatedRoute: any;
  data: any;
  activeContract = true;
  loaded = false;
  nState: StateService;
  nTranslate: TranslateService;
  nModalService: NgxSmartModalService;
  nRouter: Router;
  nVault: CoolLocalStorage;
  directLoaded = false;
  formGroup = new UntypedFormGroup({
    firstname: new UntypedFormControl(''),
    lastname: new UntypedFormControl(''),
    email: new UntypedFormControl(''),
    address: new UntypedFormGroup({
      address: new UntypedFormControl(''),
      city: new UntypedFormControl(''),
      state: new UntypedFormControl(''),
    })
  });
  styleSubscription: Subscription;
  styles: Styles = {
    colors: {}
  };
  modalOpen = false;
  loadingForm = false;

  constructor(private http: HttpClient,
              iconRegistry: MatIconRegistry,
              sanitizer: DomSanitizer,
              activatedRoute: ActivatedRoute,
              apiService: ApiService,
              modalService: NgxSmartModalService,
              translate: TranslateService,
              vault: CoolLocalStorage,
              router: Router,
              route: ActivatedRoute,
              state: StateService,
              errorService: FieldErrorService,
              analytics: AnalyticsService,
              pageTitle: Title,
              visitorService: VisitorService,
              _customerService: CustomerService,
              _renderer2: Renderer2,
              _iconRegistry: MatIconRegistry,
              _sanitizer: DomSanitizer,
              @Inject('Window') window: Window,
              @Inject(DOCUMENT) document: Document,
              private _hostEl: ElementRef) {
    super(apiService, activatedRoute, modalService, translate, vault, router, route, state, errorService, analytics, pageTitle, visitorService, _customerService, _renderer2, _iconRegistry, _sanitizer, window, document);
    const availableLanguages = [];
    Object.keys(LANGUAGES).forEach(key => {
      availableLanguages.push(LANGUAGES[key]);
    });
    // Add available languages
    translate.addLangs(availableLanguages);
    state.translationId$.subscribe(id => {
      if (id !== '') {
        AsyncTranslationLoader.translationIdSubject.next(id);
      }
    });

    this.loadAssets();

    const ua = navigator.userAgent;
    const isMacSafari = /Safari/.test(ua)
      && !/Chrome|Chromium/.test(ua); // CriOS (iOS Chrome) also uses WebKit — include it in the fix
    if (isMacSafari) {
      document.documentElement.classList.add('is-safari-mac');
      AppComponent.fixContainForSafari(document, 'inline-webbooker, popup-widget, webbooker-widget');
    }

    if(window.document.getElementsByTagName('webbooker-widget').length > 0) {
      this.formConfig = 'widget'
      this.showbreadcrumbs = false;
      this.showlogo = false;
    }

    if (!document.querySelector('meta[name="referrer"]')) {
      const meta = document.createElement('meta');
      meta.name = 'referrer';
      meta.content = 'origin-when-cross-origin';
      document.head.appendChild(meta);
    }

    this.isWidget = true;
    this.nState = state;
    this.nTranslate = translate;
    this.nModalService = modalService;
    this.nActivatedRoute = activatedRoute;
    this.nRouter = router;
    this.nVault = vault;

    if (this.nActivatedRoute.snapshot && this.nActivatedRoute.parent) {
      const dataPointer = (this.nActivatedRoute.snapshot.params.formId ? this.nActivatedRoute.data : this.nActivatedRoute.parent.data);
      this.formSubscription = dataPointer.subscribe((data: { form: any }) => {
        this.formId = data.form.id;
        this.language = LANGUAGES[this.nActivatedRoute.snapshot.paramMap.get('language') || translate.getBrowserLang()];
        const currentLanguage = this.nState.language;
        this.loaded = true;
        this.loadingForm = true;
        // Defer until constructor finishes so subclass field overrides (navigation, formStyle) are applied.
        // setDefaultLang is also deferred to avoid NG0100 — it emits synchronous langChange events
        // that cause TranslatePipe to re-evaluate mid Angular CD cycle.
        setTimeout(() => {
          this.nTranslate.setDefaultLang(currentLanguage);
          this.initFromResolvedData(data.form);
        }, 0);
      });
    }
  }

  ngOnChanges(values) {
    this.guardNavigationStyle(values);

    if (this.formid && !this.loaded) {
      this.formId = this.formid;
      this.nState.formId = this.formId;
      // Mark loaded immediately to prevent re-entry if subsequent attribute changes
      // (e.g. language='nl') trigger another ngOnChanges before the timeout fires.
      this.loaded = true;
      // Defer language resolution and form loading so that all @Input() attributes
      // (Angular Elements sets them one-by-one via attributeChangedCallback) are
      // applied before we read this.language.
      setTimeout(() => {
        const langKey = (this.language === 'auto_detect' || !this.language)
          ? this.nTranslate.getBrowserLang()
          : this.language;
        const currentLanguage = LANGUAGES[langKey] || LANGUAGES['en'];
        this.nTranslate.setDefaultLang(currentLanguage);
        this.nState.language = currentLanguage;
        if (!(this.nActivatedRoute && this.nActivatedRoute.snapshot && this.nActivatedRoute.parent)) {
          this.loadForm();
        }
      }, 0);
    } else {
      this._onChanges(values);
    }
  }

  loadForm() {
    this.loadingForm = true;
    const api = new ApiService(this.http, this.nState, 'forms', this.formId);

    // Detect integration context - use origin input if provided
    // Extract only the main domain (origin) from URL for privacy
    // window.location.origin returns the string 'null' in sandboxed iframes (opaque origin);
    // fall back to document.referrer so the host page URL is still captured correctly.
    const rawOrigin = window.location.origin;
    const parentOrigin = (!rawOrigin || rawOrigin === 'null')
      ? (document.referrer ? new URL(document.referrer).origin : '')
      : rawOrigin;
    const integrationContext = this.widgettype
      ? { loadType: this.widgettype, origin: this.origin, parentUrl: parentOrigin }
      : { ...this.nState.detectIntegrationContext(), origin: this.origin };

    return api.getCustom(`/forms/${this.formId}`).subscribe((form: any) => {
      const controls: any = {
        product: new UntypedFormControl(null),
        returnTrip: new UntypedFormControl(false),
      };
      // const group = new FormGroup(controls);
      // this.formGroup = group;
      // this.nState.formGroup = group;

      this.data = {};
      this.data.form = form;
      this.data.currency = form.config.currency;
      this.data.formId = form.id;
      this.data.timeZone = form.config.timeZone;
      this.nState.timeZone = form.config.timeZone;
      this.nState.formConfig = form.config;
      this.nState.currency = this.data.currency;

      if (form.config.styles) {
        this.data.formStyles = form.config.styles;
      }
      if (form) {
        this.nState.translationId = form.config.translationId ? form.config.translationId : environment.translationId;
        this.styles = form.config.styles;
        this.setColors();

        return api.getCustom(`/forms/companySettings`, {
          loadType: integrationContext.loadType,
          origin: integrationContext.origin,
          url: integrationContext.parentUrl
        }).subscribe((result: any) => {
          this.nState.companySettings = result.settings;
          // Populate getSettings input/output attribute
          this.getSettings = result.settings;
          this.getSettingsChange.emit(result.settings);
          return api.getCustom(`/forms/validateCloudConnectContract`).subscribe((result: any) => {
            this.nState.contractStatus = result.status;
            if (result.status === 'not_active' || result.status === 'email_not_validated') {
              this.activeContract = false;
            } else if (form) {
              this.nState.realm = result.realm;
              this.nState.googleApiKey = result.googleKey;
              this.nState.contractType = result.type;
              this._ngOnInit(this.data);
            } else {
              this.activeContract = false;
              return EMPTY;
            }
          });
        });
      } else {
        // this.router.navigate([`/not-found`]);
        return EMPTY;
      }
    });
  }

  /**
   * Initialises the form using data already fetched by FormResolverService.
   * Avoids duplicate HTTP calls to /forms, /companySettings, and /validateCloudConnectContract.
   * Must be called after the constructor chain completes (via setTimeout) so that subclass
   * field overrides such as navigation='modal' and formStyle='widget' are in effect.
   */
  initFromResolvedData(form: any): void {
    this.data = {};
    this.data.form = form;
    this.data.currency = form.config.currency;
    this.data.formId = form.id;
    this.data.timeZone = form.config.timeZone;
    this.nState.timeZone = form.config.timeZone;
    this.nState.formConfig = form.config;
    this.nState.currency = this.data.currency;

    if (form.config.styles) {
      this.data.formStyles = form.config.styles;
    }

    this.nState.translationId = form.config.translationId ? form.config.translationId : environment.translationId;
    this.styles = form.config.styles;
    this.setColors();

    // Use companySettings already fetched and stored by FormResolverService
    this.getSettings = this.nState.companySettings;
    this.getSettingsChange.emit(this.nState.companySettings);

    this._ngOnInit(this.data);
  }

  navigate(direction: string, index: number = -1): void {

    const self = this;
    const currentType = this.form.config.parts[(self.navigation === 'modal' ? this.modalFormIndex : this.selectedFormIndex)].type;
    const indexSpecified = index > -1;
    let goingBack = false;
    let goingForward = false;


    let newIndex = (indexSpecified ? index : (direction === 'next' ? this.selectedFormIndex + 1 : this.selectedFormIndex - 1));
    if (self.navigation === 'modal') {
      newIndex = (indexSpecified ? index : (direction === 'next' ? this.modalFormIndex + 1 : this.modalFormIndex - 1));
    }

    const newType = this.form.config.parts[newIndex].type;
    if (self.navigation === 'modal') {
      goingBack = this.modalFormIndex > newIndex;
      goingForward = this.modalFormIndex < newIndex;
    } else {
      goingBack = this.selectedFormIndex > newIndex;
      goingForward = this.selectedFormIndex < newIndex;
    }
    const returnTrip = this.formGroup.controls.returnTrip.value;
    const hasProduct = this.formGroup.controls['product'].value !== null;
    const fn = this.form.config.parts[newIndex].before;
    let checkPromise = Promise.resolve();

    // console.log(`new type: ${newType}`);
    if (fn) {
    }

    if (goingForward) {
      self.checkPageValidity(null, (newIndex - 1));

    }

    if ((!self.pageStates[(newIndex - 1)] && self.navigation !== "tab") && goingForward) {
      console.error('FORM NOT VALID, NOT NAVIGATING!');
    } else {
      if (currentType === 'products' && goingBack) {
        if(hasProduct) {
          this.formGroup.controls['product'].setValue(null);
          this.formGroup.controls.returnTrip.setValue(false);
        }

        if (self.navigation === 'modal' && this.formConfig === 'widget') {
          this.nModalService.close(`productSelection_${this.productSelectionCode}${this.instanceId}`);
        } else {
          this[(self.navigation === 'modal' ? 'modalFormIndex' : 'selectedFormIndex')] = newIndex;
          // Keep the step header (selectedFormIndex) in lockstep with the modal body
          // (modalFormIndex) when leaving the products step BACKWARDS, mirroring the
          // forward-nav fixes below. Without this the body returns to Pickup while the
          // header stays stuck on "Pricing" (inline-webbooker modal, formStyle!=='widget').
          if (self.navigation === 'modal' && self.formStyle !== 'widget') {
            this.selectedFormIndex = newIndex;
          }
          this.scrollUp();
        }
      }
      else {
        if (newType === 'products' && goingBack && !returnTrip) {
          this.pageStates[newIndex] = false;
        }
        else if (currentType === 'products' && !goingBack) {
          this.pageStates[newIndex] = false;
          this.processDatesForOrder();

          if(this.formGroup.controls.returnTrip.value && !this.acceptedPrice) {
            checkPromise = this.checkQuotation();
          }
        }
        else if(newType === 'products' && goingBack) {
          this.acceptedPrice = false;
        }
        /**
         * We need to correct the requestedDate setup
         * Are we showing the destinationdate? Then we put it in the requested date
         */
        checkPromise.then(() => {
          if(newIndex === 0 && this.state.orderMode == 'airport' && this.state.airportDestination &&
            this.formGroup.controls['requestedDestinationDate'] && this.formGroup.controls['requestedDestinationDate'].value) {
            this.formGroup.controls['requestedDate'].setValue(this.formGroup.controls['requestedDestinationDate'].value);
          }

          if (self.navigation === "tab") {
            self.nRouter.navigate([]).then(() => {
              let key = this.nState.storageKeys.values;
              let data = this.nVault.getItem(key);
              let object: any;
              object = JSON.parse(data);
              if (this.activeTab) {
                object.activeTab = this.activeTab;
              }

              if(this.form.config.navigateTo) {
                object.navigateTo = this.form.config.navigateTo;
              }
              window.open(`${environment.orderFormUrl}/${self.nState.language}/${this.form.id}?preOrder=${encodeURIComponent(JSON.stringify(object))}`, '_blank');
            });
          } else if (fn) {
            this[fn]().then(() => {
              this.modalFormIndex = newIndex;
              // Keep selectedFormIndex in lockstep with modalFormIndex. The step header AND the
              // selectedFormIndex===0-gated "Route/By the hour" tab are bound to selectedFormIndex,
              // while the modal body slider uses modalFormIndex. For the inline-webbooker with
              // navigation='popup'/'modal' these previously desynced on forward nav: only
              // modalFormIndex advanced, so the modal opened with the header stuck on Pickup and
              // the step-0 tab leaking into the modal, pushing the products out of view. (The
              // popup-widget, formStyle==='widget', already set this via the old else branch.)
              this.selectedFormIndex = newIndex;

              if (self.navigation === 'modal' && this.formStyle !== 'widget') {
                if (this.modalFormIndex === 1) {
                  const modal = this.nModalService.getModal(`productSelection_${this.productSelectionCode}${this.instanceId}`);
                  modal.addCustomClass('productSelectionModal');
                  modal.open();
                }
              }
              this.scrollUp();
              this.trackStepView(newIndex, goingForward);
            }, (error) => {
              // Negative action: a before-step (e.g. quotation) failed, blocking navigation.
              this.analytics.umamiEvent('navigation_failed', this.analyticsContext({
                step: this.form.config.parts[newIndex]?.gtmVirtualPageUrl,
                stepIndex: newIndex,
                reason: (error && (error.title || error.message || error.name)) || 'before_step_failed'
              }));
              console.error('ERROR IN BEFORE', error);
              this.navigationError = error;
              this.nModalService.getModal(`navigationErrorModal${this.instanceId}`).open();
              this.loader = false;
            });
          } else {

            if (self.navigation === 'modal' && self.formStyle === 'widget' && newIndex === 0) {
              const modalId = `productSelection_${self.productSelectionCode}${self.instanceId}`;
              const modal = self.nModalService.getModal(modalId);
              modal.addCustomClass('productSelectionModal');
              // Reset modal top to a fixed value before opening so the modal covers the viewport
              // regardless of where the widget element sits on the page. In Safari the
              // container-type fix makes the overlay truly viewport-relative, so a large
              // --wb-modal-top (computed from getBoundingClientRect in ngAfterViewInit) would
              // push the dialog off-screen and collapse the nsm-body to zero height ("small bar").
              document.documentElement.style.setProperty('--wb-modal-top', '50px');
              // Fire the Umami `form_load` only on a real closed->open transition (the button
              // click), not when navigating back to step 0 inside an already-open modal. The
              // script-load (widget mount) was recorded as `widget_load` (see createUmami).
              const wasOpen = this.modalOpen;
              self.nModalService.open(modalId);
              this.modalOpen = true;
              if (!wasOpen) {
                this.analytics.umamiPageView('form_loaded', this.analyticsContext());
              }
              // The step header is driven by selectedFormIndex while the form body is driven
              // by modalFormIndex. closeModal() only resets modalFormIndex, so on reopen
              // selectedFormIndex would stay on the step we left off — desyncing the header
              // from the body and breaking the forward validity check (FORM NOT VALID).
              // Keep them in lockstep when (re)opening the widget at step 0.
              this.selectedFormIndex = newIndex;
            }
            this[(self.navigation === 'modal' ? 'modalFormIndex' : 'selectedFormIndex')] = newIndex;
            // Inline-webbooker + navigation='modal' (formStyle!=='widget'): for steps without a
            // before-fn the ternary above only advances modalFormIndex, so keep selectedFormIndex
            // in lockstep too (see the fn-branch note) or the modal header desyncs on later steps.
            if (self.navigation === 'modal' && self.formStyle !== 'widget') {
              this.selectedFormIndex = newIndex;
            }
            this.scrollUp();
            this.trackStepView(newIndex, goingForward);
          }
        }).catch((error) => {
          console.error('navigate error:', error);
        });
      }
    }
  }

  /**
   * Reset the scroll position to the top of the widget when moving between wizard steps.
   * The base OrderComponent.scrollUp() scrolls the window to 0,0 which is correct for the
   * standalone app (it owns the whole page) but wrong for embedded widgets, which is why the
   * base navigate() isn't used here. Two distinct scroll contexts need handling:
   *   - navigation === 'modal': the step content scrolls INSIDE ngx-smart-modal's .nsm-content
   *     container (overflow: hidden auto), independent of the page — reset THAT to the top.
   *   - inline/scroll: the widget sits partway down a host page we don't control, so scrolling
   *     the window to 0 would jump above the widget. Bring the widget's own top edge (progress
   *     bar) into view instead.
   * Deferred by a tick so it runs after the new step has rendered (mirrors the base).
   */
  scrollUp(): void {
    setTimeout(() => {
      if (this.navigation === 'modal') {
        const modalContent = this.document.querySelector('.nsm-dialog .nsm-content') as HTMLElement | null;
        if (modalContent) {
          modalContent.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
          return;
        }
      }
      const host = this._hostEl && this._hostEl.nativeElement;
      if (host && typeof host.scrollIntoView === 'function') {
        host.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } else {
        (this.document.defaultView || window).scroll({ behavior: 'smooth', left: 0, top: 0 });
      }
    }, 100);
  }

  /**
   * Mirror the widget's wizard step to analytics. GTM gets the per-step virtual page
   * view (the destination gtmVirtualPageUrl); the Umami funnel is driven instead by a
   * `<step>_loaded` event fired when the user ARRIVES at a step (forward nav only), so
   * the widget funnel chains form_loaded -> product_selection_loaded -> ... ->
   * booking_completed. The base navigate() does the same for the standalone form.
   */
  private trackStepView(index: number, goingForward: boolean): void {
    const part = this.form?.config?.parts?.[index];
    if (part && part.gtmVirtualPageUrl) {
      this.analytics.pageView(
        `/${this.nState.language}/${this.form.id}/${part.gtmVirtualPageUrl}`,
        undefined,
        true,
        this.analyticsContext({ step: part.gtmVirtualPageUrl, stepIndex: index })
      );
    }
    if (goingForward) {
      this.emitStepLoaded(index);
    }
  }

  closeMyModal = () => {
    this.nModalService.close(`productSelection_${this.productSelectionCode}${this.instanceId}`);
  }

  closeModal = () => {
    const self = this;
    setTimeout(() => {
      self.modalOpen = false;
      self.formGroup.controls['product'].setValue(null);
      self.formGroup.controls.returnTrip.setValue(false);
      self.pageStates = [];
      self.modalFormIndex = 0;
      // Reset selectedFormIndex too — it drives the step header. Leaving it stale desyncs
      // the header from the body on the next open and breaks navigation (see navigate()).
      self.selectedFormIndex = 0;
    }, 400);
  }

  setColors(): void {
    // @ts-ignore
    const defaultConfig = require('../../assets/default_css.json');

    if (this.styles.colors) {
      Object.keys(defaultConfig).forEach(key => {
        if (!this.styles.colors[key]) {
          // Embedded integrations adopt the host page's font: unless the form config explicitly
          // specifies a font-family, don't force the default Arial. Setting the property to
          // `inherit` on <html> makes it resolve to the guaranteed-invalid value, so every
          // `font-family: var(--form-fontstyle)` becomes invalid-at-computed-value-time; because
          // font-family is an inherited property it then falls back to the parent's value,
          // cascading up through the widget root to the surrounding website's font.
          if (key === 'form-fontstyle') {
            this.document.documentElement.style.setProperty('--form-fontstyle', 'inherit');
          } else if (defaultConfig[key].indexOf('$') > -1) {
            // console.log(`--${key}: var(--${defaultConfig[key].replace('$', '')})`);
            this.document.documentElement.style.setProperty(`--${key}`, `var(--${defaultConfig[key].replace('$', '')})`);
          } else {
            // console.log(`--${key}: ${defaultConfig[key]}`);
            this.document.documentElement.style.setProperty(`--${key}`, defaultConfig[key]);
          }
        }
      });
      var ccc = new ColorContrastChecker();
      if (this.headercolor) {
        this.styles.colors['global-title-color'] = this.headercolor;

        let color1 = "#FFFFFF";
        let color2 = this.headercolor;
        var l1 = ccc.hexToLuminance('000000'); /* higher value */
        var l2 = ccc.hexToLuminance(color1); /* lower value */
        var l3 = ccc.hexToLuminance(color2); /* lower value */

        if (ccc.isLevelAA(color1, color2, 14) || ccc.getContrastRatio(l2, l3) > 4) {
          this.styles.colors['header-nav-link-active-color-inverse'] = this.headercolor;
        } else {
          this.styles.colors['header-nav-link-active-color-inverse'] = '#000000';
          this.styles.colors['form-nav-button-text-color'] = '#000000';
          this.styles.colors['form-nav-back-button-text-color'] = '#000000';
          this.styles.colors['product-book-button-text-color'] = '#000000';
        }
      }
      if (this.textcolor) {
        this.styles.colors['header-text-color'] = this.textcolor;
        this.styles.colors['global-text-color'] = this.textcolor;
        let color1 = "#FFFFFF";
        let color2 = this.textcolor;
        if (ccc.isLevelAA(color1, color2, 14)) {
          this.styles.colors['input-text-color'] = this.textcolor;
        } else {
          this.styles.colors['input-text-color'] = '#000000';
        }
      }
      if (this.backgroundcolor) {
        this.styles.colors['form-nav-background-color'] = this.backgroundcolor;
      }
      if (!this.showlogo || this.showlogo === 'false') {
        this.styles.colors['logo-display'] = 'none';
      } else {
        this.styles.colors['logo-display'] = 'inline-grid';
      }
      if (!this.showtopbar) {
        this.styles.colors['header-display'] = 'none';
      } else {
        this.styles.colors['header-display'] = 'flex';
      }
      if (!this.showlogo && !this.showbreadcrumbs) {
        this.styles.colors['top-display'] = 'none';
      } else {
        this.styles.colors['top-display'] = 'block';
      }
      // console.log('#############');
      // console.log(this.showlogo);
      // console.log(this.showtopbar);
      // console.log(this.showbreadcrumbs);

      // Applied before the styles.colors pass, so an explicit colours entry can still override
      // a single property of the chosen preset.
      applyMobileTheme(this.document, this.styles.mobileTheme);

      Object.keys(this.styles.colors).forEach(key => {
        if (this.styles.colors[key].indexOf('$') > -1) {
          this.document.documentElement.style.setProperty(`--${key}`, `var(--${this.styles.colors[key].replace('$', '')})`);
        } else {
          this.document.documentElement.style.setProperty(`--${key}`, this.styles.colors[key]);
        }
      });
    }
  }

  private _modalOffsetListener: (() => void) | null = null;

  private updateModalOffset(): void {
    if (this.modalOpen) return;
    // Guard against overwriting --wb-modal-top when another widget instance's modal is open.
    // Multiple widgets on the same page each register scroll/resize listeners independently,
    // so one instance's listener can fire and corrupt --wb-modal-top while a sibling modal is open.
    if (document.querySelector('.nsm-overlay-open')) return;
    const top = Math.max(125, this._hostEl.nativeElement.getBoundingClientRect().top);
    document.documentElement.style.setProperty('--wb-modal-top', `${top}px`);
  }

  ngAfterViewInit(): void {
    this.updateModalOffset();
    this._modalOffsetListener = () => this.updateModalOffset();
    window.addEventListener('scroll', this._modalOffsetListener, { passive: true });
    window.addEventListener('resize', this._modalOffsetListener, { passive: true });
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
    if (this._modalOffsetListener) {
      window.removeEventListener('scroll', this._modalOffsetListener);
      window.removeEventListener('resize', this._modalOffsetListener);
    }
  }
}
